import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const WeatherContext = createContext(null);

export const useWeather = () => useContext(WeatherContext);

export const DEFAULT_CITY = "भोपाल";
const DEFAULT_COORDS = { lat: 23.2599, lon: 77.4126 };

const codeMap = [
  { code: 0, hi: "साफ़ मौसम", en: "Clear", icon: "fa-sun" },
  { code: 1, hi: "मुख्यतः साफ़", en: "Mostly clear", icon: "fa-sun" },
  { code: 2, hi: "आंशिक बादल", en: "Partly cloudy", icon: "fa-cloud-sun" },
  { code: 3, hi: "घने बादल", en: "Overcast", icon: "fa-cloud" },
  { code: 45, hi: "कोहरा", en: "Fog", icon: "fa-smog" },
  { code: 48, hi: "पाला पड़ना", en: "Rime fog", icon: "fa-smog" },
  { code: 51, hi: "हल्की बूंदा-बांदी", en: "Light drizzle", icon: "fa-cloud-rain" },
  { code: 53, hi: "बूंदा-बांदी", en: "Drizzle", icon: "fa-cloud-rain" },
  { code: 55, hi: "तेज़ बूंदा-बांदी", en: "Heavy drizzle", icon: "fa-cloud-rain" },
  { code: 56, hi: "बूंदा-बांदी", en: "Freezing drizzle", icon: "fa-cloud-rain" },
  { code: 57, hi: "बूंदा-बांदी", en: "Freezing drizzle", icon: "fa-cloud-rain" },
  { code: 61, hi: "हल्की बारिश", en: "Light rain", icon: "fa-cloud-showers-heavy" },
  { code: 63, hi: "बारिश", en: "Rain", icon: "fa-cloud-showers-heavy" },
  { code: 65, hi: "तेज़ बारिश", en: "Heavy rain", icon: "fa-cloud-showers-heavy" },
  { code: 66, hi: "बारिश", en: "Freezing rain", icon: "fa-cloud-showers-heavy" },
  { code: 67, hi: "बारिश", en: "Freezing rain", icon: "fa-cloud-showers-heavy" },
  { code: 71, hi: "हल्की बर्फबारी", en: "Light snow", icon: "fa-snowflake" },
  { code: 73, hi: "बर्फबारी", en: "Snow", icon: "fa-snowflake" },
  { code: 75, hi: "तेज़ बर्फबारी", en: "Heavy snow", icon: "fa-snowflake" },
  { code: 77, hi: "बर्फ के दाने", en: "Snow grains", icon: "fa-snowflake" },
  { code: 80, hi: "बौछारें", en: "Rain showers", icon: "fa-cloud-showers-heavy" },
  { code: 81, hi: "बौछारें", en: "Rain showers", icon: "fa-cloud-showers-heavy" },
  { code: 82, hi: "तेज़ बौछारें", en: "Violent showers", icon: "fa-cloud-showers-heavy" },
  { code: 85, hi: "बर्फ़ीली बौछारें", en: "Snow showers", icon: "fa-snowflake" },
  { code: 86, hi: "बर्फ़ीली बौछारें", en: "Snow showers", icon: "fa-snowflake" },
  { code: 95, hi: "आंधी-तूफान", en: "Thunderstorm", icon: "fa-bolt" },
  { code: 96, hi: "तूफान", en: "Thunderstorm", icon: "fa-bolt" },
  { code: 99, hi: "तेज़ तूफान", en: "Severe thunderstorm", icon: "fa-bolt" },
];

export const getWeatherInfo = (code) =>
  codeMap.find((c) => c.code === code) || { hi: "मौसम", en: "Cloudy", icon: "fa-cloud" };

const weekdayHi = ["रविवार", "सोमवार", "मंगलवार", "बुधवार", "गुरुवार", "शुक्रवार", "शनिवार"];

export const WeatherProvider = ({ children }) => {
  const [city, setCity] = useState(DEFAULT_CITY);
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchWeatherByCoords = useCallback(async (lat, lon, displayName) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
          `&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m` +
          `&daily=weather_code,temperature_2m_max,temperature_2m_min,sunrise,sunset&forecast_days=4&timezone=auto`
      );
      if (!res.ok) throw new Error("Weather fetch failed");
      const data = await res.json();

      const currentInfo = getWeatherInfo(data.current.weather_code);
      const now = new Date(data.current.time);
      const isDay =
        !!data.daily?.sunrise?.[0] &&
        !!data.daily?.sunset?.[0] &&
        now >= new Date(data.daily.sunrise[0]) &&
        now <= new Date(data.daily.sunset[0]);

      setWeather({
        city: displayName || DEFAULT_CITY,
        temp: Math.round(data.current.temperature_2m),
        condition: currentInfo.hi,
        icon: currentInfo.icon,
        code: data.current.weather_code,
        isDay: isDay ? 1 : 0,
        humidity: Math.round(data.current.relative_humidity_2m) + "%",
        wind: Math.round(data.current.wind_speed_10m) + " km/h",
      });

      const dayMap = new Map();
      const today = new Date().toISOString().slice(0, 10);
      (data.daily.time || []).forEach((dateStr, idx) => {
        if (dateStr === today) return;
        const info = getWeatherInfo(data.daily.weather_code[idx]);
        dayMap.set(dateStr, {
          day: weekdayHi[new Date(dateStr).getDay()],
          temp: Math.round(data.daily.temperature_2m_max[idx]) + "°",
          icon: info.icon,
        });
      });
      setForecast([...dayMap.values()].slice(0, 3));
    } catch (err) {
      setError(err.message || "मौसम लोड करने में त्रुटि");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWeatherByCity = useCallback(
    async (name) => {
      setLoading(true);
      setError("");
      try {
        const geoRes = await fetch(
          `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(name)}&count=1&language=hi&format=json`
        );
        if (!geoRes.ok) throw new Error("City not found");
        const geo = await geoRes.json();
        if (!geo.results || geo.results.length === 0) throw new Error("शहर नहीं मिला");
        const loc = geo.results[0];
        setCity(loc.name);
        await fetchWeatherByCoords(loc.latitude, loc.longitude, loc.name);
      } catch (err) {
        setError(err.message || "मौसम लोड करने में त्रुटि");
        setLoading(false);
      }
    },
    [fetchWeatherByCoords]
  );

  useEffect(() => {
    const savedCity = localStorage.getItem("mandakini_weather_city");
    if (savedCity) {
      fetchWeatherByCity(savedCity);
    } else {
      fetchWeatherByCoords(DEFAULT_COORDS.lat, DEFAULT_COORDS.lon, DEFAULT_CITY);
    }
  }, [fetchWeatherByCoords, fetchWeatherByCity]);

  const changeCity = (name) => {
    const value = name.trim();
    if (!value) return;
    localStorage.setItem("mandakini_weather_city", value);
    setCity(value);
    fetchWeatherByCity(value);
  };

  return (
    <WeatherContext.Provider
      value={{ city, weather, forecast, loading, error, changeCity, fetchWeatherByCoords, fetchWeatherByCity }}
    >
      {children}
    </WeatherContext.Provider>
  );
};
